import React from 'react';
import PropTypes from 'prop-types';

import {Avatar, Box, Checkbox, Chip, Stack, Tooltip, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import moment from 'moment';
import {displayCode} from 'utils/backend/loom/job/job_adapter';

const JobCandidateCard = (props) => {
  const {candidate, isSelected, onSelectionChange} = props;
  const caregiverName = `${candidate.firstName} ${candidate.lastName}`;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 56px minmax(160px, 1.2fr) 90px minmax(170px, 1fr) minmax(200px, 1.3fr) 110px 160px 190px 170px',
        gap: 1.5,
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: isSelected ? '#FFF7F4' : candidate.selectable ? 'transparent' : '#F9F8F6',
        px: 2,
        py: 1.5,
      }}
    >
      <Checkbox
        label=""
        ariaLabel={translate('career.jobDetail.Candidates.Select', {name: caregiverName})}
        checked={isSelected}
        disabled={!candidate.selectable}
        onChange={onSelectionChange}
        formControlSx={{m: 0}}
        checkBoxSx={{p: 0.5}}
      />
      <Avatar alt={caregiverName} src={candidate.profileImageUrl} sx={{width: 44, height: 44}} />
      <Stack spacing={0.25}>
        <Typography fontWeight={600}>{caregiverName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {
            candidate.phoneNumber ||
            <Translate expression="career.jobDetail.Candidates.NoPhone" />
          }
        </Typography>
      </Stack>
      <Typography variant="body2">
        {
          candidate.distanceKm === null ?
            <Translate expression="career.jobDetail.Candidates.NoDistance" /> :
            <Translate expression="career.jobDetail.Candidates.Distance" substitute={{distance: candidate.distanceKm.toFixed(1)}} />
        }
      </Typography>
      <Stack direction="row" sx={{flexWrap: 'wrap', gap: 0.5}}>
        {
          candidate.languages.length === 0 &&
          <Typography variant="body2" color="text.secondary">
            <Translate expression="career.jobDetail.Candidates.None" />
          </Typography>
        }
        {
          candidate.languages.map((language) => (
            <Chip
              key={language.name}
              size="small"
              variant={language.spoken ? 'filled' : 'outlined'}
              color={language.spoken ? 'success' : language.level === 'REQUIRED' ? 'error' : 'default'}
              label={language.level === 'NICE_TO_HAVE' ?
                `${displayCode(language.name)} (${translate('job.ClientJobs.NiceToHave')})` :
                displayCode(language.name)}
            />
          ))
        }
      </Stack>
      <Stack direction="row" sx={{flexWrap: 'wrap', gap: 0.5}}>
        {
          candidate.careNeeds.length === 0 &&
          <Typography variant="body2" color="text.secondary">
            <Translate expression="career.jobDetail.Candidates.None" />
          </Typography>
        }
        {
          candidate.careNeeds.map((need) => (
            <Tooltip key={need.skill} title={need.category ? displayCode(need.category) : ''}>
              <Chip
                size="small"
                variant={need.hasSkill ? 'filled' : 'outlined'}
                color={need.hasSkill ? 'success' : 'default'}
                label={need.interviewed ?
                  `${displayCode(need.skill)} · ${translate('career.jobDetail.Candidates.Interviewed')}` :
                  displayCode(need.skill)}
              />
            </Tooltip>
          ))
        }
      </Stack>
      <Typography variant="body2">
        <Translate
          expression="career.jobDetail.Candidates.Slots"
          substitute={{covered: candidate.availability.slotsCovered, total: candidate.availability.slotsTotal}}
        />
      </Typography>
      <Stack spacing={0.25}>
        <Typography variant="body2">
          {
            candidate.travel.mainTravelMethod ?
              displayCode(candidate.travel.mainTravelMethod) :
              <Translate expression="career.jobDetail.Candidates.NotGiven" />
          }
        </Typography>
        {
          candidate.travel.maxTravelTime &&
          <Typography variant="body2" color="text.secondary">
            <Translate
              expression="career.jobDetail.Candidates.MaxTravelTime"
              substitute={{time: displayCode(candidate.travel.maxTravelTime).toLowerCase()}}
            />
          </Typography>
        }
      </Stack>
      <Stack spacing={0.25}>
        {
          candidate.interviews.length === 0 &&
          <Typography variant="body2" color="text.secondary">
            <Translate expression="career.jobDetail.Candidates.None" />
          </Typography>
        }
        {
          candidate.interviews.map((interview) => (
            <Typography key={`${interview.type}-${interview.completedAt}`} variant="body2">
              {
                interview.type ?
                  displayCode(interview.type) :
                  <Translate expression="career.jobDetail.Candidates.Interview" />
              }
              {`, ${moment(interview.completedAt).format('MMM D, YYYY')}`}
            </Typography>
          ))
        }
      </Stack>
      <Stack spacing={0.25}>
        {
          candidate.selectable &&
          <Typography variant="body2" color="success.main">
            <Translate expression="career.jobDetail.Candidates.CanInvite" />
          </Typography>
        }
        {
          !candidate.selectable &&
          candidate.blockedReasons.map((reason) => (
            <Typography key={reason} variant="body2" color="text.secondary">
              <Translate expression={`career.jobDetail.Candidates.BlockedReasons.${reason}`} />
              {
                reason === 'ALREADY_INVITED' && candidate.invitation &&
                ` (${translate(`career.jobDetail.Invitations.Status.${candidate.invitation.status}`)})`
              }
            </Typography>
          ))
        }
      </Stack>
    </Box>
  );
};

JobCandidateCard.propTypes = {
  candidate: PropTypes.shape({
    caregiverId: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string,
    profileImageUrl: PropTypes.string,
    distanceKm: PropTypes.number,
    selectable: PropTypes.bool.isRequired,
    blockedReasons: PropTypes.arrayOf(PropTypes.string).isRequired,
    invitation: PropTypes.shape({
      id: PropTypes.string,
      status: PropTypes.string,
    }),
    languages: PropTypes.array.isRequired,
    careNeeds: PropTypes.array.isRequired,
    availability: PropTypes.shape({
      slotsCovered: PropTypes.number,
      slotsTotal: PropTypes.number,
    }).isRequired,
    travel: PropTypes.shape({
      mainTravelMethod: PropTypes.string,
      maxTravelTime: PropTypes.string,
    }).isRequired,
    interviews: PropTypes.array.isRequired,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

export default React.memo(JobCandidateCard);
